<template>
  <div>
    <z-header :title="title" :actions="actions"></z-header>

    <section>
      <div class="columns is-multiline is-mobile">
        <div class="column is-6">
          <b-field label="Período">
            <b-datepicker
              placeholder="Selecione as datas..."
              icon="calendar"
              v-model="filters.dates"
              @input="loadData()"
              range
              trap-focus
            >
            </b-datepicker>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Motoristas">
            <b-select
              placeholder="Filtre por motorista"
              expanded
              v-model="filters.user"
              icon="user"
              @input="loadData()"
            >
              <option
                v-for="option in users"
                :value="option.id"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Veículo">
            <b-autocomplete
                v-model="filters.registrationPlate"
                name="registrationPlate"
                maxlength="8"
                icon="car"

                @input="loadData()"
                clearable
                :data="registrationPlate"
                :keep-first="true"
                @select="option => (selected = option)"
                >
                <template #empty>No results found</template>
            </b-autocomplete>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Departamentos">
            <b-select
              placeholder="Filtre por departamento"
              expanded
              v-model="filters.department"
              icon="user-friends"
              @input="loadData()"
            >
              <option
                v-for="option in departments"
                :value="option"
                :key="option"
              >
                {{ option }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
      <div>
        <p class="has-text-right">
          <button @click="exportToCSV" :class="{'is-loading' : isLoading ,'is-primary': true, button: true }" :disabled="data.length == 0">Exportar para CSV</button>
        </p>
      </div>
    </section>

    <section>
      <b-table
        :data="data"
        :hoverable="true"
        :striped="true"
        :row-class="rowClass"
        :mobile-cards="true"
        :loading="isLoading"
        default-sort="date"
        default-sort-direction="desc"
        sort-icon="sort-up"
        :paginated="true"
        :pagination-simple="true"
        aria-next-label="Proxima página"
        aria-previous-label="Página anterior"
        aria-page-label="Página"
        aria-current-label="Página atual"
      >
        <template slot-scope="props">
          <b-table-column field="date" label="Data" sortable>{{
            props.row.date | dateTime
          }}</b-table-column>

          <b-table-column field="username" label="Usuário" sortable>{{
            props.row.userName
          }}</b-table-column>

          <b-table-column field="registrationPlate" label="Veículo" sortable>
            {{ props.row.registrationPlate }}
          </b-table-column>

          <b-table-column field="description" label="Descrição" sortable>{{
            props.row.description
          }}</b-table-column>

          <b-table-column field="amount" label="Valor" sortable>{{
            ((props.row.type == "DEBIT" ? -1 : 1) * props.row.amount) | money
          }}</b-table-column>

          <b-table-column field="city" label="Cidade" sortable>{{
            getCityName(props.row.city)
          }}</b-table-column>

          <b-table-column field="locale" label="Localização" centered>
            <a target="_blank" v-if="props.row.lat && props.row.lng" :href="toMapsLink(props.row.lat,props.row.lng)">
            <b-icon
                pack="fas"
                icon="map-marked-alt"
                size="is-small">
            </b-icon>
            </a>
          </b-table-column>
        </template>
        <template slot="empty">
          <section v-if="!isLoading" class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <b-icon icon="sad-tear" size="is-large"></b-icon>
              </p>
              <p>Sem dados da frota.</p>
            </div>
          </section>
        </template>
      </b-table>
    </section>
  </div>
</template>

<script>
import { subMonths } from "date-fns";
import { getCityName } from "../../services/cityUtils";

import ZHeader from "../../layout/Header";
import reports from "../../services/reports";
import fleet from "../../services/fleetUser";
import { downloadCSV } from "../../services/downloadCSV";
import { toMapsLink } from "../../services/mapsLink";
import { rawDateTime } from '../../filters/dateTime';

import "../../filters/dateTime";
import "../../filters/money";

export default {
  data() {
    return {
      title: "Extrato",
      actions: [],
      data: [],
      users: [],
      departments: [],
      plates: [],
      isLoading: false,
      firstLoad: true,
      filters: {}
    };
  },
  computed: {
    registrationPlate() {
      return this.plates.filter(plate => {
        if(!this.filters.registrationPlate) return false;
        return plate.toUpperCase().includes(this.filters.registrationPlate.toUpperCase());
      });
    }
  },
  mounted() {
    this.setFilterList();
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      const {
        dates: [startDate, endDate]
      } = this.filters;

      const filters = {
        startDate: rawDateTime(new Date(startDate), "yyyy-MM-dd"),
        endDate: rawDateTime(new Date(endDate), "yyyy-MM-dd"),
        fleetUserId: this.filters.user || null,
        department: this.filters.department || null,
        registrationPlate: this.filters.registrationPlate || null,
      };

      reports
        .getStatement(
          filters
        )
        .then(data => {
          this.isLoading = false;
          this.data = data;
        });
    },
    loadUsers() {
      fleet.all(999, 1).then(data => {
        this.users = data;
      });
    },
    loadDepartments() {
      reports.getDepartments().then(data => {
        this.departments = data;
      });
    },
    loadRegistrationPlates() {
      reports.getFleetVehicles().then(data => {
        this.plates = data.map(item => item.registrationPlate);
      });
    },
    toMapsLink() {
      return toMapsLink(...arguments);
    },
    rowClass() {
      return "is-clickable";
    },
    setFilterList() {
      this.loadUsers();
      this.loadDepartments();
      this.loadRegistrationPlates();

      this.filters.dates = [subMonths(new Date(), 1), new Date()];
    },
    exportToCSV(e) {
      e.preventDefault();

      const header = {
        id: "ID",
        date: "Data",
        type: "Tipo",
        amount: "Valor",
        description: "Item",
        registrationPlate: "Veículo",
        vehicleType: "Tipo Veículo",
        vehicleExternalCode: "Cód. Interno Veículo",
        userDocument: "Documento",
        userName: "Usuário",
        userExternalCode: "Cód. Interno Usuário",
        userDepartment: "Departamento",
        city: "Cidade",
        latLong: "Localização"
      };

      const allowedCols = Object.keys(header);
      const filename = `extrato-zul-corporate-${rawDateTime(new Date(), "dd-MM-yyyy-HH-mm")}`;

      const csv = this.data.map(row =>
        allowedCols
          .map(col => {
            switch (col) {
              case "date":
                return rawDateTime(row.date);
              case "type":
                return row.type == "DEBIT" ? "Débito" : "Crédito";
              case "vehicleType":
                switch(row.vehicleType) {
                  case "CAR":
                    return "Carro";
                  case "MOTORCYCLE":
                    return "Moto";
                  case "TRUCK":
                    return "Caminhão";
                  case "MOTORCYCLE_COURIER":
                    return "Motofrete";
                  default:
                    return row.vehicleType;
                }
              case "amount":
                return (row.type == "DEBIT" ? -1 : 1) * row.amount;
              case "latLong":
                return toMapsLink(row.lat, row.lng)
              default:
                return row[col];
              case "city":
                return this.getCityName(row.city)
            }
          })
          .join(";")
      );

      const file = `${allowedCols.map(h => header[h]).join(";")}\n${csv.join("\n")}`;
      // Download CSV file
      downloadCSV(file, `${filename}.csv`);
    },
    getCityName(cityValue) {
        return getCityName(cityValue);
    },
  },
  components: {
    ZHeader
  }
};
</script>

<style lang="scss"></style>
